import {
  RateType,
  RutterExportState,
  SettlementState,
  SettlementStateEvent,
} from '@treadinc/horizon-api-spec';

import { SettlementLineItemGroup } from '~hooks/useSettlementLineItems/models';
import { Settlement } from '~hooks/useSettlements/models';
import { ROUTES } from '~router/routePaths';
import isEnumValue from '~utils/isEnumValue';

/**
 * Builds a URL to search the Files/Jobs approval page with the given search term
 */
export const buildFilesSearchUrl = (searchTerm: string) => {
  return `/${ROUTES.approvals.base}/${ROUTES.approvals.approveJobs}?search=${encodeURIComponent(searchTerm)}`;
};

export const canSendEmail = (settlement: Settlement) => {
  const emailableStatuses = [
    SettlementState.APPROVED,
    SettlementState.CUSTOMER_APPROVED,
    SettlementState.PAID,
    SettlementState.CUSTOMER_PAID,
  ];

  return emailableStatuses.includes(settlement.state);
};

export const canVoidSettlement = (settlement: Settlement) => {
  return (
    settlement.stateEvents.includes(SettlementStateEvent.VOID) &&
    settlement.state !== SettlementState.VOIDED
  );
};

export const canRebillSettlement = (settlement: Settlement) => {
  return settlement.rebillable && settlement.state !== SettlementState.VOIDED;
};

export const canExportToAccounting = (
  settlement: Settlement,
  isAccountingExportEnabled: boolean,
) => {
  const exportableStatuses = [
    SettlementState.APPROVED,
    SettlementState.CUSTOMER_APPROVED,
    SettlementState.PAID,
    SettlementState.CUSTOMER_PAID,
  ];

  const hasExportableStatus = exportableStatuses.includes(settlement.state);

  if (isAccountingExportEnabled) {
    // the accounting export rejects driver settlements (driver_settlement_not_supported)
    if (settlement.driver) {
      return false;
    }

    const accountingExport = settlement.rutterSettlementExport;
    // can export if not yet exported or the export failed (can retry)
    return (
      hasExportableStatus &&
      (!accountingExport ||
        accountingExport.state === RutterExportState.NOT_EXPORTED ||
        accountingExport.state === RutterExportState.FAILED)
    );
  }

  // No accounting integration: can export if not yet manually exported
  return hasExportableStatus && !settlement.exportedAt;
};

// The bulk export endpoint rejects a request with more ids than this.
export const MAX_BULK_EXPORT_SETTLEMENTS = 100;

// Driver settlements are excluded from accounting export.
export const canBulkExportToAccounting = (settlement: Settlement) =>
  settlement.driver === null && canExportToAccounting(settlement, true);

const rateTypeIsUsuallyBackedByTimesheet = {
  [RateType.RATE_COMMISSION]: false,
  [RateType.RATE_FLAT_COMMISSION]: false,
  [RateType.RATE_FLAT_RATE]: true,
  [RateType.RATE_PER_BUSHEL]: false,
  [RateType.RATE_PER_DAY]: false,
  [RateType.RATE_PER_EACH]: false,
  [RateType.RATE_PER_HOUR]: true,
  [RateType.RATE_PER_LOAD]: false,
  [RateType.RATE_PER_POUND]: false,
  [RateType.RATE_PER_TON]: false,
  [RateType.RATE_PER_TONNE]: false,
  [RateType.RATE_PER_YARD]: false,
} satisfies Record<RateType, boolean>;

export const lineItemGroupsHaveTimesheetRateType = (
  lineItemGroups: SettlementLineItemGroup[],
) =>
  lineItemGroups.some((group) =>
    group.lineItems.some((lineItem) =>
      lineItem.rateTypes.some(
        (rateType) =>
          isEnumValue(RateType, rateType) && rateTypeIsUsuallyBackedByTimesheet[rateType],
      ),
    ),
  );

export interface DownloadDialogState {
  isLoading: boolean;
  isOpen: boolean;
  preselectTimesheet: boolean;
  settlementIds: string[];
}

export const CLOSED_DOWNLOAD_DIALOG_STATE: DownloadDialogState = {
  isLoading: false,
  isOpen: false,
  preselectTimesheet: false,
  settlementIds: [],
};
